import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';

const TR_DAYS = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDayLabel(dateStr, lang) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return lang === 'tr' ? TR_DAYS[d.getDay()] : EN_DAYS[d.getDay()];
  } catch {
    return dateStr;
  }
}

function CustomTooltip({ active, payload, label, t, lang: _lang }) {
  if (!active || !payload || !payload.length) return null;
  const value = payload[0]?.value ?? 0;
  return (
    <div
      className="bg-white border-2 border-gray-200 rounded-xl px-3 py-2 text-xs shadow-md"
      role="tooltip"
    >
      <p className="font-semibold text-[var(--text-main)] mb-0.5">{label}</p>
      <p className="text-[var(--brand-primary)]">
        {t('cabin.progress.weekly.tooltip_label')}: <span className="font-bold">{value}</span>
      </p>
    </div>
  );
}

export default function WeeklyActivityChart({ data = [] }) {
  const { t, lang } = useLanguage();

  const chartData = data.map((d) => ({
    name: d.day
      ? (lang === 'tr' ? d.day : (d.day_en || d.day))
      : getDayLabel(d.date, lang),
    // backend sends `completed` (lessons); adapter maps it to `miles` for compat
    // we read `completed` first, then fall back to `miles`
    lessons: d.completed ?? d.miles ?? 0,
  }));

  const hasData = chartData.some((d) => d.lessons > 0);

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-4">
      <h3 className="text-sm font-bold text-[var(--text-main)] mb-3">
        {t('cabin.progress.weekly.title')}
      </h3>

      {/* Screen reader fallback table */}
      <table
        aria-label={t('cabin.progress.weekly.aria_label')}
        className="sr-only"
      >
        <thead>
          <tr>
            <th>{lang === 'tr' ? 'Gün' : 'Day'}</th>
            <th>{lang === 'tr' ? 'Tamamlanan Ders' : 'Completed Lessons'}</th>
          </tr>
        </thead>
        <tbody>
          {chartData.map((row) => (
            <tr key={row.name}>
              <td>{row.name}</td>
              <td>{row.lessons}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Visual chart */}
      <div aria-hidden="true" style={{ height: 160 }}>
        {!hasData ? (
          <div className="flex items-center justify-center h-full text-xs text-[var(--text-muted)]">
            {lang === 'tr' ? 'Bu hafta henüz aktivite yok' : 'No activity this week yet'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#777777' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#777777' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomTooltip t={t} lang={lang} />}
                cursor={{ stroke: '#E5E5E5', strokeWidth: 1 }}
              />
              <Line
                type="linear"
                dataKey="lessons"
                stroke="var(--brand-primary)"
                strokeWidth={2.5}
                dot={{ fill: 'var(--brand-primary)', r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
